import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { AdminDepartmentService } from '../../app/admin/departments/service';
import { handleError } from '../functions/handleError';
import { IDepartment } from '../interfaces/department/IDepartment';
import { IUser } from '../interfaces/user/IUser';
import { usePagination } from './usePagination';

export const useDepartment = () => {
  const {
    pagination,
    page,
    count,
    setPagination,
    totalPages,
    setTotalPages,
    handleUpdateList,
    handlePages,
    setCount,
    changeFilterHandler,
  } = usePagination({
    perPage: 15,
  });
  const { id } = useParams();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [department, setDepartment] = useState<IDepartment | null>(null);
  const [data, setData] = useState<IDepartment[]>([]);
  const [selectedDepartment, setSelectedDepartment] =
    useState<IDepartment | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [isAddModalOpened, setIsAddModalOpened] = useState(false);
  useState<IDepartment | null>(null);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);

  const deleteHandler = async (departmentId: number) => {
    await AdminDepartmentService.delete(departmentId)
      .then((response) => {
        if (response) {
          handleUpdateList();
          enqueueSnackbar(
            t('toasts.successfullyDeleted', {
              title: selectedDepartment?.name,
            }),
            {
              variant: 'success',
            },
          );
          closeDeleteModal();
        }
      })
      .catch((err) => handleError(err));
  };

  const closeDeleteModal = () => {
    setSelectedDepartment(null);
    setSelectedUser(null);
    setShowDeleteModal(false);
  };

  const handleCreateDepartment = async () => {
    await AdminDepartmentService.create({
      ...selectedDepartment,
      userIds: selectedUserIds,
    } as IDepartment)
      .then((response) => {
        if (response) {
          setSelectedDepartment(null);
          setIsAddModalOpened(false);
          setSelectedUserIds([]);
          enqueueSnackbar(
            t('toasts.successfullyCreated', { title: response?.name }),
            {
              variant: 'success',
            },
          );
          handleUpdateList();
        }
      })
      .catch((err) => handleError(err));
  };

  const deleteUserFromDepartmentHandler = (
    departmentId?: number,
    userId?: number,
  ) => {
    !!departmentId &&
      !!userId &&
      AdminDepartmentService.deleteUser(departmentId, userId)
        .then((response) => {
          if (response) {
            setDepartment(
              (prev) =>
                ({
                  ...prev,
                  users: prev?.users?.filter((user) => user.id !== userId),
                }) as IDepartment,
            );
            enqueueSnackbar(
              t('toasts.successfullyDeleted', {
                title: `${selectedUser?.firstName} ${selectedUser?.lastName}`,
              }),
              {
                variant: 'success',
              },
            );
            setShowDeleteModal(false);
          }
        })
        .catch((err) => handleError(err));
  };

  const handleUpdateEmployees = async () => {
    !!department?.id &&
      (await AdminDepartmentService.update(
        {
          ...department,
          userIds: selectedUserIds,
        },
        department?.id,
      )
        .then((response) => {
          if (response) {
            setDepartment(response);
            enqueueSnackbar(
              t('toasts.successfullyUpdated', { title: department?.name }),
              {
                variant: 'success',
              },
            );
            setIsAddModalOpened(false);
          }
        })
        .catch((err) => handleError(err)));
  };

  useEffect(() => {
    AdminDepartmentService.get({ ...pagination, page: pagination?.page + 1 })
      .then((response) => {
        setCount(response?.count);
        setTotalPages(Math.ceil(response?.count / response?.perPage));
        setData(response.data);
      })
      .catch((err) => handleError(err));
  }, [pagination, setCount, setTotalPages]);

  useEffect(() => {
    !!id &&
      AdminDepartmentService.getSingle(Number(id))
        .then((response) => {
          setDepartment(response);
          setSelectedUserIds(response?.users?.map((user) => user?.id));
        })
        .catch((err) => handleError(err));
  }, [id]);
  return {
    // Data and Pagination State
    data,
    selectedUser,
    department,
    setDepartment,

    // Pagination Handlers
    pagination,
    totalPages,
    count,
    page,
    setPagination,
    handlePages,
    changeFilterHandler,

    // Filter State and Handlers
    selectedDepartment,
    setSelectedDepartment,

    // Modal State and Handlers
    isAddModalOpened,
    setIsAddModalOpened,
    showDeleteModal,
    setShowDeleteModal,
    setSelectedUser,
    closeDeleteModal,

    // Selected User IDs State
    selectedUserIds,
    setSelectedUserIds,

    // Action Handlers
    deleteUserFromDepartmentHandler,
    handleCreateDepartment,
    deleteHandler,
    handleUpdateEmployees,
  };
};
